import React, { useEffect, useRef } from "react";

//hooks
import { useDrag, useDrop } from "react-dnd";
import useDetectClick from "../../../../../hooks/useDetectClick";
import { useDragAndDropControls } from "./DragAndDropControls.hooks";

//interfaces
import { IDragAndDropRow } from "../../../../../interfaces/tables/ISimpleTable";
import { IconLibrary } from "../../../../../interfaces/icon/IconLibrary";

//components
import Icon from "../../../icons/Icon";
import SelectButtonList from "../../../lists/SelectButtonList";
import { ISelectButton } from "../../../../../interfaces/lists/ISelectButtonList";

const DragAndDropRow = (
    { 
        containerRef, 
        row, 
        index, 
        hoverIndex, 
        controlIndex, 
        setControlIndex, 
        moveRow,
        rowContentLength, 
        setHoverIndex, 
        openMenu, 
        setOpenMenu, 
        dragAndDropAction, 
        isPaginated, 
        paginationInformation
    }: IDragAndDropRow) => {
    
    const ref = useRef<HTMLTableRowElement>(null);
    const controlCellRef = useRef<HTMLTableCellElement>(null)
    const menuRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<HTMLButtonElement>(null);

    const dragAndDropControls = useDragAndDropControls({
        index,
        rowContentLength,
        paginationInformation,
        isPaginated,
        handleButtonMove
    });

    // Drag logic
    const [{ isDragging }, drag, preview] = useDrag({
        type: "ROW",
        item: { index },
        collect: (monitor) => ({ isDragging: monitor.isDragging()}),
        end: () => { setHoverIndex(null) }
    });
    drag(dragRef);

    const [, drop] = useDrop({
        accept: "ROW",
        hover(item: { index: number }) {
            if (!ref.current) { return }

            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) { return }

            moveRow(dragIndex, hoverIndex, false);
            setHoverIndex(hoverIndex);
            item.index = hoverIndex;
        },
        
        drop() {
            setHoverIndex(null);
            dragAndDropAction?.(hoverIndex ?? 0)
        }
    });

    useEffect(() => {
        preview(drop(ref));
    }, [preview, drop]);

    useEffect(() => {
        document.body.style.cursor = isDragging ? "grabbing" : "inherit";
    }, [isDragging]);


    useEffect(() => {
        const handleDragLeave = (event: DragEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.relatedTarget as Node)) {
                setHoverIndex(null);
            }
        };
        containerRef?.current.addEventListener('dragleave', handleDragLeave); 
        return () => containerRef?.current.addEventListener('dragleave', handleDragLeave); 
    }, [containerRef]);

    useDetectClick("control-cell", (clickedInCell) => {
        if (!clickedInCell) setOpenMenu(false);
    });

    function handleButtonMove(index: number, newIndex: number, isNewPageMove?: boolean) {
        dragAndDropAction?.(newIndex);
        moveRow(index, newIndex, isNewPageMove);
    }

    return (
        <tr ref={ref} className={`drag-item ${isDragging ? "is-dragging" : ""}  ${index === hoverIndex ? "is-hovering" : ""}`}>
            {row.content.map((cell: any, cellIndex: number) => (
                <td key={cellIndex}>{cell}</td>
            ))}

            <td className="rc-control-cell"  ref={controlCellRef}>
                <button 
                    type='button' 
                    ref={dragRef} 
                    className="drag-and-drop-controller" 
                    aria-hidden="true" 
                    tabIndex={-1}
                >
                    <Icon iconName={IconLibrary.DRAG} color="#999EA5" sizeInPx="12"/>
                </button>

                <button 
                    type='button' 
                    className="simple-table-menu" 
                    aria-label="Open menu" 
                    onClick={() => { setOpenMenu(!openMenu); setControlIndex(index); }} 
                    tabIndex={0}
                > 
                    <Icon 
                        iconName={IconLibrary.MENU_MORE} 
                        color="#343D4C" 
                        sizeInPx="12"
                    />
                </button>
                        
                {controlIndex === index && openMenu && (
                    <div ref={menuRef}>
                        <SelectButtonList
                            className="drag-and-drop-list"
                            allButtonOnClick={() => setOpenMenu(false)}
                            buttons={[...dragAndDropControls, ...(row.controls ?? [])].filter(Boolean) as ISelectButton[]}
                        />
                    </div>
                )}
            </td>
        </tr>
    );
};

export default DragAndDropRow;
